/**
 * ============================================================================
 * Customer Insights Formatters - Business logic utilities
 * ============================================================================
 *
 * Extracted from JSX for testability and reusability
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from './currency';
import type { Customer } from '../types/customerInsights';

/**
 * Format currency amount
 */
export function formatCurrency(amount: number | string | undefined | null): string {
  const num = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0);
  if (isNaN(num)) return `${CURRENCY_SYMBOL}0`;
  return `${CURRENCY_SYMBOL}${num.toLocaleString('en-NG', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

/**
 * Format date
 */
export function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return 'N/A';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-NG', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return 'N/A';
  }
}

/**
 * Format retention rate
 */
export function formatRetentionRate(rate: number | undefined | null): string {
  const safeRate = rate ?? 0;
  return `${safeRate.toFixed(1)}%`;
}

/**
 * Get customer segment display info
 */
export function getSegmentInfo(segment: string): {
  label: string;
  colorClass: string;
} {
  const segmentMap: Record<string, { label: string; colorClass: string }> = {
    vip: { label: 'VIP', colorClass: 'bg-purple-100 text-purple-700 border-purple-200' },
    regular: { label: 'Regular', colorClass: 'bg-blue-100 text-blue-700 border-blue-200' },
    member: { label: 'Member', colorClass: 'bg-green-100 text-green-700 border-green-200' },
    occasional: { label: 'Occasional', colorClass: 'bg-gray-100 text-gray-700 border-gray-200' },
  };

  return segmentMap[segment] || segmentMap.occasional;
}

/**
 * Get customer type badge info
 */
export function getTypeInfo(isNew: boolean): {
  label: string;
  colorClass: string;
} {
  return isNew
    ? { label: 'New', colorClass: 'bg-green-100 text-green-700' }
    : { label: 'Returning', colorClass: 'bg-blue-100 text-blue-700' };
}

/**
 * Export customers to CSV
 */
export function exportCustomersToCSV(customers: Customer[]): void {
  if (customers.length === 0) {
    throw new Error('No customers to export');
  }

  const headers = [
    'Name',
    'Email',
    'Phone',
    'Total Bookings',
    'Memberships',
    'Total Spent',
    'Last Activity',
    'Customer Type',
    'Active Member',
    'Segment',
  ];

  const rows = customers.map((customer) => [
    customer.name || 'N/A',
    customer.email || 'N/A',
    customer.phone || 'N/A',
    (customer.total_bookings || 0).toString(),
    (customer.membership_count || 0).toString(),
    (customer.total_spent || 0).toFixed(2),
    customer.last_booking_date || 'N/A',
    customer.is_new ? 'New' : 'Returning',
    customer.has_membership ? 'Yes' : 'No',
    getSegmentInfo(customer.segment).label,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
  ].join('\n');

  // Download CSV
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `customer_insights_${new Date().toISOString().split('T')[0]}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
